import {
  disableScroll,
  enableScroll,
  debounce
} from 'application/utils/helpers'

/**
 * Класс для управления всплывающим окном поиска
 * Обеспечивает открытие/закрытие попапа, обработку ввода и переход к результатам поиска
 */
export class SearchPopup {
  /** Кнопка переключения состояния попапа */
  private toggle: HTMLElement
  /** Элемент всплывающего окна поиска */
  private popup: HTMLElement
  /** Поле ввода текста для поиска */
  private input: HTMLInputElement
  /** Контейнер с популярными запросами */
  private popularContainer: HTMLElement
  /** Флаг, указывающий открыто ли окно поиска */
  private isOpen: boolean = false

  /** Текущий поисковый запрос */
  public query: string = ''

  /**
   * Создает экземпляр компонента поиска
   * @param {HTMLElement} popup - DOM элемент всплывающего окна поиска
   */
  constructor(popup: HTMLElement) {
    this.popup = popup
  }

  /**
   * Инициализирует компонент поиска
   * Находит необходимые элементы и подключает обработчики событий
   */
  public init(): void {
    this.toggle = document.querySelector<HTMLElement>(
      '[data-search-toggle]'
    ) as HTMLElement

    this.input = this.popup.querySelector<HTMLInputElement>(
      '[data-search-input] input'
    ) as HTMLInputElement

    this.popularContainer = this.popup.querySelector<HTMLElement>(
      '[data-search-popular]'
    ) as HTMLElement

    this.toggle.addEventListener('click', this.onToggleClick)

    const debouncedInput = debounce(this.onInputChange, 800)
    this.input.addEventListener('input', debouncedInput)

    this.popularContainer.addEventListener('click', this.onPopularClick)
  }

  /**
   * Обработчик клика по кнопке переключения состояния попапа
   * Открывает или закрывает окно поиска в зависимости от текущего состояния
   */
  private onToggleClick = () => {
    if (this.isOpen) {
      this.closePopup()
    } else {
      this.openPopup()
    }
  }

  /**
   * Открывает всплывающее окно поиска
   * Блокирует прокрутку страницы и добавляет обработчики событий
   */
  private openPopup(): void {
    if (this.isOpen) return

    disableScroll()
    this.popup.classList.add('is-open')
    document.addEventListener('keydown', this.onEscapePress)
    setTimeout(() => {
      document.addEventListener('click', this.onClickOutside)
    })

    this.isOpen = true
    this.changeToggleState()
    // Сразу ставим курсор в поле: иначе после клика по лупе печатать нельзя,
    // фокус остаётся на кнопке. Поле видимо с первого кадра открытия — см.
    // .search-popup.is-open в search-popup.scss, иначе focus() не сработает.
    this.input.focus({ preventScroll: true })
  }

  /**
   * Закрывает всплывающее окно поиска
   * Восстанавливает прокрутку страницы и удаляет обработчики событий
   */
  private closePopup(): void {
    if (!this.isOpen) return

    enableScroll()
    this.popup.classList.remove('is-open')
    document.removeEventListener('keydown', this.onEscapePress)
    document.removeEventListener('click', this.onClickOutside)

    this.isOpen = false
    this.changeToggleState()
    // Возвращаем фокус на лупу, если он остался внутри закрытого окна
    if (this.popup.contains(document.activeElement)) {
      this.toggle.focus({ preventScroll: true })
    }
  }

  /**
   * Обработчик нажатия клавиши Escape
   * Закрывает окно поиска при нажатии на Escape
   * @param {KeyboardEvent} event - Событие клавиатуры
   */
  private onEscapePress = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.closePopup()
    }
  }

  /**
   * Обработчик клика вне окна поиска
   * Закрывает попап при клике за его пределами
   * @param {MouseEvent} event - Событие мыши
   */
  private onClickOutside = (event: MouseEvent): void => {
    const target = event.target as HTMLElement
    if (target.closest('[data-search-popup]')) return

    this.closePopup()
  }

  /**
   * Изменяет состояние кнопки переключения попапа
   * Обновляет текст и иконку в зависимости от состояния окна поиска
   */
  private changeToggleState(): void {
    const textElement = this.toggle.querySelector<HTMLElement>('span')
    const iconElement = this.toggle.querySelector<HTMLElement>('svg')
    textElement!.textContent = this.isOpen ? 'Закрыть поиск' : 'Открыть поиск'
    iconElement!.innerHTML = this.isOpen
      ? '<use xlink:href="#close" />'
      : '<use xlink:href="#search" />'
  }

  /**
   * Обработчик изменения содержимого поля ввода
   * Запускает поиск с задержкой после ввода текста
   * @param {Event} event - Событие изменения поля ввода
   */
  private onInputChange = (event: Event): void => {
    const target = event.target as HTMLInputElement
    this.query = target.value

    if (this.query.length === 0) return

    this.goToSearch(this.query)
  }

  /**
   * Обработчик клика по популярному запросу
   * Переходит на страницу поиска с выбранным популярным запросом
   * @param {MouseEvent} event - Событие мыши
   */
  private onPopularClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement
    const popularQuery =
      target.getAttribute('data-action') === 'popular-query'
        ? target.textContent || ''
        : ''

    if (popularQuery.length === 0) return

    this.goToPopular(popularQuery.trim())
  }

  /**
   * Переход на страницу результатов поиска
   * @param {string} query - Поисковый запрос
   */
  public goToSearch(query: string): void {
    const searchPath = this.popup.dataset.searchUrl || '/search'
    window.location.href = `${searchPath}?query=${encodeURIComponent(query)}`
  }

  /**
   * Переход на страницу поиска с популярным запросом
   * @param {string} popularQuery - Популярный поисковый запрос
   */
  public goToPopular(popularQuery: string): void {
    this.goToSearch(popularQuery)
  }
}

/**
 * Инициализирует компонент всплывающего окна поиска
 * Находит элемент попапа на странице и создает экземпляр класса SearchPopup
 */
export function initSearchPopup(): void {
  const searchElement = document.querySelector<HTMLElement>(
    '[data-search-popup]'
  )

  if (searchElement) {
    new SearchPopup(searchElement).init()
  }
}
